import { ProductDeal, ProductSummary, AccountSummary, DashboardData, ProductFilter } from '../types'

class ProductsService {
  private rawData: ProductDeal[] = []
  private isLoaded = false

  private async loadData(): Promise<void> {
    if (this.isLoaded) return

    try {
      const response = await fetch('/products/closed_deals_products.csv')
      if (!response.ok) {
        throw new Error('Failed to fetch products data')
      }
      
      const csvText = await response.text()
      this.rawData = this.parseCSV(csvText)
      this.isLoaded = true
    } catch (error) {
      console.error('Error loading products data:', error)
      throw error
    }
  }

  private parseCSV(csvText: string): ProductDeal[] {
    const lines = csvText.trim().split('\n')
    const data: ProductDeal[] = []

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i])
      if (values.length >= 9) {
        data.push({
          account: values[0]?.trim() || '',
          deal_id: values[1]?.trim() || '',
          deal_name: values[2]?.trim() || '',
          amount: parseFloat(values[3]?.replace(/,/g, '') || '0'),
          product_type: values[4]?.trim() || 'Not Specified',
          date_created: values[5]?.trim() || '',
          date_closed_won: values[6]?.trim() || '',
          commission_value: parseFloat(values[7]?.replace(/,/g, '') || '0'),
          deal_stage: values[8]?.trim() || ''
        })
      }
    }

    return data
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    let i = 0

    while (i < line.length) {
      const char = line[i]
      
      if (char === '"' && (i === 0 || line[i - 1] === ',')) {
        inQuotes = true
      } else if (char === '"' && inQuotes && (i === line.length - 1 || line[i + 1] === ',')) {
        inQuotes = false
      } else if (char === ',' && !inQuotes) {
        result.push(current)
        current = ''
      } else {
        current += char
      }
      i++
    }
    
    result.push(current)
    return result
  }

  private applyFilter(data: ProductDeal[], filter?: ProductFilter): ProductDeal[] {
    if (!filter) return data

    return data.filter(deal => {
      if (filter.account && deal.account !== filter.account) return false
      if (filter.product_type && deal.product_type !== filter.product_type) return false
      if (filter.search) {
        const searchLower = filter.search.toLowerCase()
        const searchableText = `${deal.deal_name} ${deal.account} ${deal.product_type}`.toLowerCase()
        if (!searchableText.includes(searchLower)) return false
      }
      if (filter.date_from) {
        const dealDate = new Date(deal.date_closed_won)
        const fromDate = new Date(filter.date_from)
        if (dealDate < fromDate) return false
      }
      if (filter.date_to) {
        const dealDate = new Date(deal.date_closed_won)
        const toDate = new Date(filter.date_to)
        if (dealDate > toDate) return false
      }
      return true
    })
  }

  async getDashboardData(filter?: ProductFilter): Promise<DashboardData> {
    await this.loadData()
    const filteredData = this.applyFilter(this.rawData, filter)

    // Calculate overall totals
    const total_deals = filteredData.length
    const total_value = filteredData.reduce((sum, deal) => sum + deal.amount, 0)
    const total_commission = filteredData.reduce((sum, deal) => sum + deal.commission_value, 0)

    // Group by account
    const accountGroups = filteredData.reduce((acc, deal) => {
      if (!acc[deal.account]) {
        acc[deal.account] = []
      }
      acc[deal.account].push(deal)
      return acc
    }, {} as Record<string, ProductDeal[]>)

    // Create account summaries
    const accounts: AccountSummary[] = Object.entries(accountGroups).map(([account, deals]) => {
      const accountTotal = deals.reduce((sum, deal) => sum + deal.amount, 0)
      const accountCommission = deals.reduce((sum, deal) => sum + deal.commission_value, 0)

      // Product breakdown for this account
      const productGroups = deals.reduce((acc, deal) => {
        if (!acc[deal.product_type]) {
          acc[deal.product_type] = { deals: [], value: 0, commission: 0 }
        }
        acc[deal.product_type].deals.push(deal)
        acc[deal.product_type].value += deal.amount
        acc[deal.product_type].commission += deal.commission_value
        return acc
      }, {} as Record<string, { deals: ProductDeal[], value: number, commission: number }>)

      const product_breakdown: ProductSummary[] = Object.entries(productGroups)
        .map(([product_type, data]) => ({
          product_type,
          total_count: data.deals.length,
          total_value: data.value,
          total_commission: data.commission,
          percentage_by_count: (data.deals.length / deals.length) * 100,
          percentage_by_value: (data.value / accountTotal) * 100
        }))
        .sort((a, b) => b.total_value - a.total_value)

      return {
        account,
        total_deals: deals.length,
        total_value: accountTotal,
        total_commission: accountCommission,
        product_breakdown
      }
    }).sort((a, b) => b.total_value - a.total_value)

    // Overall product summary
    const productGroups = filteredData.reduce((acc, deal) => {
      if (!acc[deal.product_type]) {
        acc[deal.product_type] = { deals: [], value: 0, commission: 0 }
      }
      acc[deal.product_type].deals.push(deal)
      acc[deal.product_type].value += deal.amount
      acc[deal.product_type].commission += deal.commission_value
      return acc
    }, {} as Record<string, { deals: ProductDeal[], value: number, commission: number }>)

    const product_summary: ProductSummary[] = Object.entries(productGroups)
      .map(([product_type, data]) => ({
        product_type,
        total_count: data.deals.length,
        total_value: data.value,
        total_commission: data.commission,
        percentage_by_count: (data.deals.length / total_deals) * 100,
        percentage_by_value: (data.value / total_value) * 100
      }))
      .sort((a, b) => b.total_value - a.total_value)

    // Monthly data
    const deals_by_month: { [key: string]: number } = {}
    const value_by_month: { [key: string]: number } = {}

    filteredData.forEach(deal => {
      const date = new Date(deal.date_closed_won)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      
      deals_by_month[monthKey] = (deals_by_month[monthKey] || 0) + 1
      value_by_month[monthKey] = (value_by_month[monthKey] || 0) + deal.amount
    })

    return {
      total_deals,
      total_value,
      total_commission,
      accounts,
      product_summary,
      deals_by_month,
      value_by_month
    }
  }

  async getUniqueAccounts(): Promise<string[]> {
    await this.loadData()
    return Array.from(new Set(this.rawData.map(deal => deal.account))).sort()
  }

  async getUniqueProductTypes(): Promise<string[]> {
    await this.loadData()
    return Array.from(new Set(this.rawData.map(deal => deal.product_type))).sort()
  }

  async exportData(filter?: ProductFilter): Promise<string> {
    await this.loadData()
    const filteredData = this.applyFilter(this.rawData, filter)

    if (filteredData.length === 0) {
      return 'No data to export'
    }

    const headers = ['Account', 'Deal ID', 'Deal Name', 'Amount', 'Product Type', 'Date Created', 'Date Closed Won', 'Commission Value', 'Deal Stage']
    const rows = filteredData.map(deal => [
      deal.account,
      deal.deal_id,
      `"${deal.deal_name}"`,
      deal.amount,
      deal.product_type,
      deal.date_created,
      deal.date_closed_won,
      deal.commission_value,
      deal.deal_stage
    ])

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n')
  }
}

export const productsService = new ProductsService()